import { Nullable } from 'legacy/lut/classes/models/utils'
import { mapPartnerFromPartnerAllDataRegions } from 'legacy/lut/services/partner/mapping'
import { fetchPartnerFlagsByIdAndType } from 'legacy/lut/services/partner/partnerFlags'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import {
  asianCountriesWithEspeciallyRequirements,
  PartnerFields,
  PartnerHierarchyType,
} from 'clickNgo/entities/partner/configs'
import { usePartnerInfoFormData } from 'clickNgo/entities/partner/libs/hooks/form'
import { IPartner } from 'clickNgo/entities/partner/models'
import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { FormDropdown } from 'cng-common/features/form'
import { IForm } from 'core-components/classes/models/IForm'
import { ConfirmDialog } from 'shared/components/confirmDialog'
import { FieldGroup } from 'shared/components/fieldGroup/FieldGroup'

import { partnerBasicInfoTranslation } from '../../translation'

import {
  AFFILIATE_AND_RESELLER_BUYER_TYPE_ALLOWED,
  fieldsCopyFromHQ,
  fieldsToResetAfterHierarchyTypeChange,
  hierarchyTypeListWithCopyData,
} from './configs'
import { checkTopPartnerIdVisibility } from './helpers/checkTopPartnerIdVisibility'
import { getHierarchyTypeSource } from './helpers/getHierarchyTypeSource'
import { getTopPartnerIdSource } from './helpers/getTopPartnerIdSource'
import { IHierarchyBlockProps, THierarchyTypeSource } from './models'

export const HierarchyBlock = memo<IHierarchyBlockProps>(
  ({
    readOnlyFields,
    formKeys,
    hierarchyType,
    topPartnerId,
    isNewPartner,
    isDisabled,
    isReadOnly,
    isHierarchyTypeRequired,
    isTopPartnerIdRequired,
    onHierarchyTypeValidate,
    onTopPartnerIdValidate,
  }) => {
    /** -------------------- Variables ----------------------- */

    const [selectedTopPartner, setSelectedTopPartner] = useState<Nullable<IPartner>>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [
      isAffiliateOrResellerNetworkConfirmDialogVisible,
      setIsAffiliateOrResellerNetworkConfirmDialogVisible,
    ] = useState(false)
    const [isTopPartnerIdVisible, setIsTopPartnerIdVisible] = useState(false)
    const {
      updateBasicInfoFormData,
      updateLegalInfoFormData,
      basicInfoFormData,
      basicInfoFormModifiedData,
      legalInfoFormModifiedData,
    } = usePartnerInfoFormData({ formKeys })

    const hierarchyTypeSource = useMemo(() => {
      return getHierarchyTypeSource({ region: basicInfoFormData.region, isNewPartner })
    }, [basicInfoFormData.region, isNewPartner])

    /** -------------------- Handlers ----------------------- */

    const copyDataFromTopPartner = useCallback(
      (topPartner: Nullable<IPartner>, hierarchyType: PartnerHierarchyType) => {
        if (hierarchyTypeListWithCopyData.includes(hierarchyType) && topPartner) {
          fieldsCopyFromHQ[hierarchyType].basic.forEach((field) => {
            updateBasicInfoFormData({ field, value: topPartner[field] })
          })
          fieldsCopyFromHQ[hierarchyType].legal.forEach((field) => {
            if (
              field === 'companyEnglishName' &&
              !asianCountriesWithEspeciallyRequirements.includes(topPartner.country)
            ) {
              return
            }
            updateLegalInfoFormData({ field, value: topPartner[field] })
          })
        }
      },
      [updateBasicInfoFormData, updateLegalInfoFormData]
    )

    const handleTopPartnerIdChange = useCallback(
      async (_value: string, data?: IForm) => {
        if (data) {
          const topPartner = mapPartnerFromPartnerAllDataRegions(data)

          if (
            [PartnerHierarchyType.AFFILIATE, PartnerHierarchyType.RESELLER_NETWORK].includes(
              basicInfoFormData.hierarchyType
            )
          ) {
            setIsLoading(true)
            const partnerFlags = await fetchPartnerFlagsByIdAndType({
              partnerId: topPartner.partnerId,
              type: ProfilingCodeType.Agreement,
            })

            const hasNoBuyerTypeFlags = partnerFlags.every(
              (flag) =>
                !AFFILIATE_AND_RESELLER_BUYER_TYPE_ALLOWED.some((type) =>
                  flag.code.toLowerCase().endsWith(type)
                )
            )

            if (hasNoBuyerTypeFlags) {
              updateBasicInfoFormData({
                field: PartnerFields.topPartnerId.field,
                value: '',
              })
              setSelectedTopPartner(null)
              setIsAffiliateOrResellerNetworkConfirmDialogVisible(true)
              setIsLoading(false)
              return
            }
            setIsLoading(false)
          }
          copyDataFromTopPartner(topPartner, basicInfoFormData.hierarchyType)
          setSelectedTopPartner(topPartner)
        } else {
          setSelectedTopPartner(null)
        }
      },
      [basicInfoFormData.hierarchyType, copyDataFromTopPartner, updateBasicInfoFormData]
    )

    const handleHierarchyTypeChange = useCallback(
      (_value: string, item?: THierarchyTypeSource) => {
        fieldsToResetAfterHierarchyTypeChange.basic.forEach((field) => {
          if (basicInfoFormModifiedData.includes(field)) {
            updateBasicInfoFormData({ field, value: '' })
          }
        })

        fieldsToResetAfterHierarchyTypeChange.legal.forEach((field) => {
          if (legalInfoFormModifiedData.includes(field)) {
            updateLegalInfoFormData({ field, value: '' })
          }
        })

        const isTopPartnerIdVisible = item && checkTopPartnerIdVisibility(item?.title)

        if (isTopPartnerIdVisible) {
          copyDataFromTopPartner(selectedTopPartner, item?.title || hierarchyType)
        } else {
          updateBasicInfoFormData({ field: PartnerFields.topPartnerId.field, value: '' })
          setSelectedTopPartner(null)
        }
      },
      [
        basicInfoFormModifiedData,
        updateBasicInfoFormData,
        legalInfoFormModifiedData,
        updateLegalInfoFormData,
        copyDataFromTopPartner,
        selectedTopPartner,
        hierarchyType,
      ]
    )

    const handleConfirmDialogConfirm = useCallback(() => {
      setIsAffiliateOrResellerNetworkConfirmDialogVisible(false)
    }, [])

    /** -------------------- Content ----------------------- */

    const confirmDialogMessage = useMemo(
      () =>
        basicInfoFormData.hierarchyType === PartnerHierarchyType.RESELLER_NETWORK
          ? partnerBasicInfoTranslation.confirm.resellerNetworkAvailability.content
          : partnerBasicInfoTranslation.confirm.affiliateAvailibility.content,
      [basicInfoFormData.hierarchyType]
    )

    const confirmDialogContent = useMemo(
      () => <InfoBlock text={confirmDialogMessage} type={InfoBlockType.Danger} />,
      [confirmDialogMessage]
    )

    /** -------------------- Effects ----------------------- */

    useEffect(() => {
      setIsTopPartnerIdVisible(
        checkTopPartnerIdVisibility(basicInfoFormData.hierarchyType || hierarchyType)
      )
    }, [basicInfoFormData.hierarchyType, hierarchyType])

    return (
      <div style={{ position: 'relative' }}>
        <FieldGroup title={partnerBasicInfoTranslation.content.blockTitles.hierarchyBlock} size={3}>
          <LoaderOverlay isVisible={isLoading} color="white" />
          <FormDropdown
            source={hierarchyTypeSource}
            caption="title"
            formKey={formKeys.basic}
            defaultValue={hierarchyType}
            field={PartnerFields.hierarchyType.field}
            label={PartnerFields.hierarchyType.label}
            onChange={handleHierarchyTypeChange}
            onValidate={onHierarchyTypeValidate}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly || readOnlyFields?.includes(PartnerFields.hierarchyType.field)}
            isRequired={isHierarchyTypeRequired}
            isStatic
          />
          <FormDropdown
            source={getTopPartnerIdSource()}
            caption="PartnerID"
            formKey={formKeys.basic}
            defaultValue={topPartnerId}
            field={PartnerFields.topPartnerId.field}
            label={PartnerFields.topPartnerId.label}
            onChange={handleTopPartnerIdChange}
            onValidate={onTopPartnerIdValidate}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly || readOnlyFields?.includes(PartnerFields.topPartnerId.field)}
            isRequired={isTopPartnerIdRequired || isTopPartnerIdVisible}
            isVisible={isTopPartnerIdVisible}
          />
          <ConfirmDialog
            isVisible={isAffiliateOrResellerNetworkConfirmDialogVisible}
            onConfirm={handleConfirmDialogConfirm}
            title={partnerBasicInfoTranslation.confirm.affiliateAvailibility.title}
            confirmBtnText={partnerBasicInfoTranslation.confirm.affiliateAvailibility.confirm}
            content={confirmDialogContent}
            isOnlyApply
          />
        </FieldGroup>
      </div>
    )
  }
)

HierarchyBlock.displayName = 'HierarchyBlock'
